/**
 * ==========================================================================
 *  AUDIO TUNING
 * ==========================================================================
 *  Every effect in the game is synthesised at runtime from oscillators and
 *  noise (src/audio/synth.ts), and every shouted line is spoken out loud by
 *  the browser's speech synthesis (src/audio/Voice.ts) - unless somebody has
 *  recorded that cue, in which case the recording wins (src/audio/samples.ts).
 *  With an empty samples folder the whole mixer is still a few hundred bytes
 *  of maths, downloads nothing and licenses nothing.
 *
 *  Numbers that change how the game *sounds* live here, exactly like the
 *  numbers that change how it *feels* live in gameplay.ts.
 * ==========================================================================
 */

import type { GameSound } from '../audio/AudioManager';

export const MIX = {
  /**
   * Master trim. Levelled by measuring the peak of every sound through an
   * AnalyserNode: the loudest single event (a wall at full speed) lands near
   * -11 dBFS, which leaves room for a stumble into a door with Hannu shouting.
   */
  master: 0.75,
  /** Loops fade in and out over this many seconds instead of clicking. */
  loopFade: 0.8,
  /**
   * Per-sound trim, multiplied by the volume passed at the call site.
   */
  gain: {
    /**
     * A single footfall. Nothing in the game fires this any more - the run is
     * the `running` loop below - but it stays a cue so that the recipe the
     * synthesised loop is written from has somewhere to live, and so that a
     * `sfx/footstep` recording is still a legal thing to drop in the folder.
     */
    footstep: 0.3,
    /**
     * The run itself, which is on for most of the run and therefore has to sit
     * under everything that is not. See `RUN_LOOP` for the rest of it.
     */
    running: 0.42,
    jump: 0.5,
    /**
     * Level with the take-off it answers. It is a confirmation rather than an
     * event, and a confirmation you have to strain for is not one.
     */
    'double-jump': 0.5,
    land: 0.5,
    'door-open': 0.8,
    /**
     * Level with the opening it answers. A door shut behind you is a thing
     * you chose to spend time on, and a cue you have to strain for would make
     * it feel like it had not registered.
     */
    'door-close': 0.8,
    bump: 0.7,
    stumble: 1.0,
    // Right by the ear, and it fires whenever the player wants it to.
    'beer-sip': 0.55,
    checkpoint: 0.5,
    finish: 0.6,
    'shout-finaali': 1.0,
    'kimmo-wake': 1.0,
    'kimmo-oho': 1.0,
    'hannu-emerge': 0.85,
    'hannu-shout': 1.0,
    'hannu-swipe': 0.65,
    /**
     * The cook, from across the front yard. Under the intro yell and level
     * with Hannu's: he is closer than the first and further than the second,
     * and unlike either of them he is not shouting at anybody in anger.
     */
    'shout-kokki': 0.9,
    /** Mäysä, from a doorway a stride away. Nearer than the cook, so quieter. */
    'shout-maysa': 0.8,
    /**
     * The porukka, from the middle of the juhlasali - six metres in, which is
     * a shout across a room rather than down a hall. Level with the cook: it
     * lands nearer than he does and further than Mäysä, and unlike either of
     * them it is seven people rather than one.
     */
    'shout-porukka': 0.9,
    /**
     * The party in front of the teltta, on the subject of the power. The
     * loudest shout in the game, and the only one that has to beat something:
     * it lands on top of `power-off` still ringing out, from further away than
     * any of the three above are - and it is a yard of people rather than one
     * mouth. Under `kimmo-shout`, which is the only voice closer than this.
     */
    'shout-teltta': 1.0,
    /**
     * Kempf, from a stride away and not raising his voice. The quietest of the
     * five and by some distance: every other one of them is somebody hailing
     * you across a yard or a hall, and this is a man saying something he would
     * rather not have to say, standing close enough to touch.
     */
    'shout-kempf': 0.6,
    /**
     * And the handle. Level with `bump`, which is the other cue that means
     * "that did not work" - it is a latch and a leaf rather than a shoulder
     * and a wall, but it is answering the same question and it should not have
     * to be listened for.
     */
    'ovi-lukossa': 0.7,
    /**
     * The tuoppi off the table and the hana into it. Both are at arm's length
     * in a quiet room, so both are trimmed well down - the hall has karaoke
     * two doors away and a projector running, and a pour that carried over
     * those would be a pour happening somewhere other than in your hand.
     *
     * The pour is the quieter of the two despite being a second longer. It is
     * the confirmation the whole errand was for, and a confirmation that
     * arrives as a noise you have to stop for is one that costs time.
     */
    'tuoppi-poydalta': 0.55,
    'bottle-break': 0.65,
    'hana-auki': 0.4,
    /**
     * The two answers at the muurikka. Both are right by the ear and both fire
     * under a clock, so they are short and they are trimmed to sit over the
     * yard rather than on top of it - the one that has to carry is the wrong
     * one, because that is the one that costs you something.
     */
    'makkara-lautaselle': 0.6,
    oksennus: 0.85,
    /**
     * The klapi. Both of these happen at arm's length while everything else in
     * the yard is happening, so they are quiet and short - what they are for is
     * telling the hands that the press landed, not telling the room.
     */
    'klapi-nosto': 0.5,
    'klapi-pesaan': 0.75,
    /**
     * The yard's power going out. Loud, and meant to be: it happens once at
     * most, it happens behind you, and it is the only warning that the room you
     * are running into has just gone dark.
     */
    'power-off': 0.9,
    /**
     * A window going in, from about a metre away, with your head through it.
     * Loud for the same reason `power-off` is - it happens once, it is the
     * loudest thing that has happened yet, and unlike the blackout it is the
     * player's own doing. Under the shout, over everything else.
     */
    'glass-break': 0.85,
    /**
     * His own throat, so it is the closest thing in the mix and the loudest
     * thing in the run. Over the smash it answers rather than under it - the
     * two are a call and a response, not one event, which is also why
     * `GLASS_SHOUT_DELAY` exists.
     */
    'kimmo-shout': 1.0,
    /**
     * The hall's opinion of what is on the screen. Loud, because it is a room
     * full of people and one of them is not being quiet about it - but under
     * the shout that is aimed at the player, because this one is not.
     */
    ajotus: 0.8,
    /**
     * Kimmo, asleep, on the main screen. Level with the snoring it interrupts
     * rather than over it: the snore is what the room sounds like and this is
     * the same man doing the same involuntary thing, so it arrives as part of
     * the ambience and not as an event the player is meant to react to. Louder
     * and it would read as something happening *to* the screen.
     */
    fart: 0.35,
    /**
     * The yard: birds, and nothing under them. See `YARD` below for where it
     * is heard, and `makeMorningAmbience` for why there is no wind bed in it.
     *
     * Levelled against the feet rather than against the peak meter, because
     * those two are the only things playing for most of a run: the birds sit
     * about 5 dB under the run loop's own level, so the thing the player is
     * doing stays on top of the place they are doing it in.
     */
    'ambient-morning': 0.32,
    // Ambience sits under everything: it is a room, not an event.
    'ambient-snoring': 0.2,
    /**
     * The ceiling the karaoke reaches, standing in the juhlasali with it. It
     * gets there through `KARAOKE` below - out in the yard this is scaled most
     * of the way to nothing. Leave room for Damu's voice over the music.
     */
    karaoke: 0.65,
    /**
     * And the ceiling the tent's PA reaches, standing in front of it. Under
     * the loudest one-shots and over everything else that runs continuously,
     * because for the three seconds this is at full level you are a metre from
     * two speaker stacks - and because it is scaled the whole way down to a
     * thump by `TELTTA_DJ` everywhere else in the level, which is where the
     * run actually spends its time.
     */
    'teltta-dj': 0.8,
    /**
     * The muurikka, standing at it. The quietest continuous thing in the game
     * and deliberately so - this is a hiss, and a hiss is the one shape of
     * sound that stops being heard and starts being *endured* if it is put
     * anywhere near the top of a mix.
     *
     * Levelled against the birds rather than against the peak meter, because
     * those are the two things playing while the plate is being filled.
     * Measured through the mixer standing at the plate, the sizzle comes out
     * 2.8 dB under `ambient-morning` - close enough to read as the nearest
     * thing in the yard, far enough that the yard is still the thing you are
     * standing in. The birds are sparse calls with a 25 dB crest and this is
     * continuous, so in the gaps between them it is the thing you hear, which
     * is what a grill across a yard is.
     *
     * The take is peak-normalised and has a 22 dB crest of its own, so most of
     * what this number scales is the fat spitting rather than the bed under it
     * - which is why it looks lower than the other ambiences and does not
     * sound it.
     *
     * This is the one number to move if it wants to be louder or quieter; the
     * shape of the approach is `MUURIKKA_SIHINA` below and is a separate
     * question.
     */
    muurikka: 0.14,
  } satisfies Record<GameSound, number>,
} as const;

/* ------------------------------------------------------ kuorsaus ja muut -- */

/* ------------------------------------------------------- juoksun tahti -- */

/* -------------------------------------------------- karaoke juhlasalissa -- */

/* ------------------------------------------------------- piha ja linnut -- */

/* --------------------------------------------------- teltan dj-setti -- */

/* ------------------------------------------------------- muurikan sihinä -- */

/* ---------------------------------------------------------- projektori -- */

/* ------------------------------------------------------ recorded samples -- */

/* Re-exported so that the call sites for the mix and the loops keep one import. */
export {
  SNORE,
  RUN_LOOP,
  KARAOKE,
  YARD,
  TELTTA_DJ,
  MUURIKKA_SIHINA,
  PROJECTOR,
} from './loops';
export {
  GLASS_SHOUT_DELAY,
  BLACKOUT_OHO_DELAY,
  BLACKOUT_SHOUT_DELAY,
  SAMPLES,
  AUDIO_SEED,
  VOICE,
  type SampleTrim,
  type VoiceStyle,
} from './voice';
